import ReactMapboxGl, { Cluster, Layer, Marker, ScaleControl, Source, ZoomControl } from 'react-mapbox-gl';
import * as MapboxGL from 'mapbox-gl';
import {
    AppBar,
    Checkbox,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { default as turflength } from '@turf/length';
import StraightenIcon from '@mui/icons-material/Straighten';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import FormControl from '@mui/material/FormControl';
import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import {
    getDashboardAgentLocations,
    getDashboardAgentQuotedLocations,
    getDashboardAgentStateLocations,
    getDashboardAgentSubmittedLocations,
    getSubmissionMapLocations,
    searchMapAddress,
} from 'services/agentDashboard';
import { DefaultTheme, withTheme, WithTheme, makeStyles, createStyles } from '@mui/styles';
import { COLORS } from 'constants/index';
import { formatTruncatedDollar } from 'helpers/formatters';
import { MapboxGeoJSONFeature } from 'mapbox-gl';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PlaceIcon from '@mui/icons-material/Place';
import LocationDetailDialog from 'components/LrProducerDashboard/components/LocationMap/components/LocationDetailDialog';
import { ToggleButton } from '@mui/lab';
import { MapLocation } from '../../../../types/location.type';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { InstallationProviderContext } from '../../../../application/Providers/Installation';

const Map = ReactMapboxGl({
    accessToken: 'pk.eyJ1IjoibHVtZW5yaXNrIiwiYSI6ImNrOHl1ampyOTFtY2EzaW52cTh2aGczajkifQ.oF3DEGyiJuTegRckt1xXqw',
});

interface LocationMapProps {
    producerId?: string;
    submissionId?: string;
    disableGrouping?: boolean;
    expandStyleClass?: string;
    productLineCode?: string;
    expanded?: boolean;
}

const LocationMap: React.FunctionComponent<LocationMapProps & WithTheme> = (props) => {
    const {
        producerId,
        submissionId,
        disableGrouping: disableGroupingProp,
        expandStyleClass,
        productLineCode,
        expanded,
        theme,
    } = props;

    const installationProvider = useContext(InstallationProviderContext);
    const [stateLocationsFetched, setStateLocationsFetched] = useState<boolean>(false);
    const [stateLocations, setStateLocations] = useState<any>({});
    const [locationsFetched, setLocationsFetched] = useState<boolean>(false);
    const [locations, setLocations] = useState<any>({});
    const [quotedLocations, setQuotedLocations] = useState<any>({});
    const [quotedLocationsFetched, setQuotedLocationsFetched] = useState<boolean>(false);
    const [submittedLocations, setSubmittedLocations] = useState<any>({});
    const [submittedLocationsFetched, setSubmittedLocationsFetched] = useState<boolean>(false);
    const [submissionLocations, setSubmissionLocations] = useState<any>({});
    const [submissionLocationsFetched, setSubmissionLocationsFetched] = useState<boolean>(false);
    const [mapState, setMapState] = useState<any>({
        zoom: [submissionId ? 4.06 : 3.06],
        center: [-97.56229, 38.42109],
    });
    const [currentZoom, setCurrentZoom] = useState<any>(0);
    const [popupLocation, setPopupLocation] = useState<any | undefined>(undefined);
    const [selectedList, setSelectedList] = useState<string[]>(submissionId ? ['SUBMISSION'] : ['INFORCE']);
    const [mapExpanded, setMapExpanded] = useState<boolean>(!!expanded);
    const [disableGrouping, setDisableGrouping] = useState<boolean>(!!disableGroupingProp);
    const [searchResultLocation, setSearchResultLocation] = useState<any>();
    const [reloadingClusters, setReloadingClusters] = useState<boolean>(false);
    const mapApi = useRef<MapboxGL.Map>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showSearch, setShowSearch] = useState<boolean>(false);

    const [measureDistancePoints, setMeasureDistancePoints] = useState<any>({
        type: 'FeatureCollection',
        features: [],
    });
    const [measureText, setMeasureText] = useState<string>('');
    const [measureMode, setMeasureMode] = useState<boolean>(false);

    useEffect(() => {
        // The Cluster component doesn't internally update its props, so changes need to cause dropping and readding the component
        if (reloadingClusters) {
            setReloadingClusters(false);
        }
    }, [reloadingClusters]);

    const zoomThreshold = selectedList.includes('QUOTED') || selectedList.includes('SUBMITTED') ? 1 : 5;

    const fetchSubmissionLocations = async () => {
        if (submissionId) {
            const submissionLocationsFetch = await getSubmissionMapLocations(submissionId, productLineCode);

            setSubmissionLocations(submissionLocationsFetch);
            setSubmissionLocationsFetched(true);
        }
    };

    const fetchStateLocations = async () => {
        const stateLocationsFetch = await getDashboardAgentStateLocations(producerId, productLineCode);
        stateLocationsFetch.features.forEach((f: MapboxGeoJSONFeature) => {
            if (f.properties && f.properties.TIV) {
                f.properties.TIV = formatTruncatedDollar(f.properties.TIV);
            }
        });
        setStateLocations(stateLocationsFetch);
        setStateLocationsFetched(true);
    };

    const fetchAllLocations = async () => {
        const stateLocationsFetch = await getDashboardAgentLocations(producerId, productLineCode);

        setLocations(stateLocationsFetch);
        setLocationsFetched(true);
    };

    const fetchQuotedLocations = async () => {
        const quotedLocationsFetch = await getDashboardAgentQuotedLocations(producerId, productLineCode);
        setQuotedLocations(quotedLocationsFetch);
        setQuotedLocationsFetched(true);
    };

    const fetchSubmittedLocations = async () => {
        const submittedLocationsFetch = await getDashboardAgentSubmittedLocations(producerId);
        setSubmittedLocations(submittedLocationsFetch);
        setSubmittedLocationsFetched(true);
    };

    useEffect(() => {
        fetchStateLocations();
        fetchAllLocations();
        fetchQuotedLocations();
        fetchSubmittedLocations();
    }, [producerId, productLineCode]);

    useEffect(() => {
        if (submissionId) {
            fetchSubmissionLocations();
        }
    }, [submissionId]);

    const onZoom = (map: MapboxGL.Map) => {
        // tslint:disable-next-line:no-console
        // console.log(map.getZoom());
        setCurrentZoom(map.getZoom());
        setPopupLocation(undefined);
    };

    useEffect(() => {
        if (locationsFetched) {
            setMapState({ zoom: [mapState.zoom[0] + 0.1], center: mapState.center });
        }
    }, [locationsFetched]);

    const onMove = (map: MapboxGL.Map) => {
        // tslint:disable-next-line:no-console
        // console.log(map.getCenter());
        // setCenter(newCenter);
        setPopupLocation(undefined);
    };

    const onSearchChange = (e: any) => {
        setSearchTerm(e.target.value);
    };

    const onMeasureToggleClick = () => {
        measureDistancePoints.features = [];
        setMeasureDistancePoints({ ...measureDistancePoints });
        setMeasureText('');

        setMeasureMode(!measureMode);
    };

    const onExpandToggleClick = () => {
        setMapExpanded(!mapExpanded);
        setTimeout(() => {
            mapApi.current?.resize();
        }, 100); // after resizing is complete the map itself needs to resize
    };

    const onSearchToggleClick = () => {
        setShowSearch(!showSearch);
        setSearchTerm('');
        setSearchResultLocation(undefined);
    };

    const doSearch = async (term: string) => {
        const searchedLocationProps = await searchMapAddress(term, productLineCode);
        const searchedLocation: any = {};
        searchedLocation.geometry = {
            coordinates: [searchedLocationProps.locationLon, searchedLocationProps.locationLat],
        };
        searchedLocation.properties = searchedLocationProps;
        setSearchResultLocation(searchedLocation);
        moveMap(searchedLocationProps.locationLon, searchedLocationProps.locationLat, 10.06);
    };

    // Used to draw a line between points
    const linestring: any = {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: [],
        },
    };

    const onMouseMove = (map: MapboxGL.Map, evt: React.SyntheticEvent<any>) => {
        if (!measureMode) {
            map.getCanvas().style.cursor = 'grab';
        } else {
            const e = (evt as unknown) as MapboxGL.MapMouseEvent; // covert the type to actual type
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['measure-points'],
            });
            map.getCanvas().style.cursor = features.length ? 'pointer' : 'crosshair';
        }
    };

    const moveMap = (lng: number, lat: number, zoom: number) => {
        mapApi.current?.easeTo({
            center: [lng, lat],
            zoom: zoom,
        });
    };

    const onClick = (map: MapboxGL.Map, evt: React.SyntheticEvent<any>) => {
        if (!measureMode) return;
        const e = (evt as unknown) as MapboxGL.MapMouseEvent; // covert the type to actual type
        const features = map.queryRenderedFeatures(e.point, {
            layers: ['measure-points'],
        });

        // Remove the linestring from the group
        // So we can redraw it based on the points collection
        if (measureDistancePoints.features.length > 1) measureDistancePoints.features.pop();

        // Clear the Distance container to populate it with a new value
        let tempMeasureText = '';

        // If a feature was clicked, remove it from the map
        if (features.length > 0) {
            const id = features[0]?.properties?.id;
            measureDistancePoints.features = measureDistancePoints.features.filter((point: any) => {
                return point?.properties?.id !== id;
            });
        } else {
            // if we already have two points, another click (not on an existing point, removes all points)
            if (measureDistancePoints.features.length > 1) measureDistancePoints.features = [];
            const point = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [e.lngLat.lng, e.lngLat.lat],
                },
                properties: {
                    id: String(new Date().getTime()),
                },
            };

            measureDistancePoints.features.push(point);
        }

        if (measureDistancePoints.features.length > 1) {
            linestring.geometry.coordinates = measureDistancePoints.features.map((point: any) => {
                return point.geometry.coordinates;
            });

            measureDistancePoints.features.push(linestring);

            // Populate the distanceContainer with total distance
            const len = turflength(linestring, { units: 'miles' });
            if (len < 1) {
                tempMeasureText = 'Total distance: ' + Math.round(5280 * len).toLocaleString() + 'ft';
            } else {
                tempMeasureText = 'Total distance: ' + len.toLocaleString() + 'mi';
            }
        }

        setMeasureDistancePoints({ ...measureDistancePoints });
        setMeasureText(tempMeasureText);
        // map.getSource('geojson').setData(geojson);
    };

    const onLocationMarkerClick = (location: any) => {
        if (measureMode) return;
        const popupObject = {
            coordinates: location.geometry.coordinates,
            ...location.properties,
        };
        setPopupLocation(popupObject);
    };

    // make sure the buttons all go where they are supposed to
    const measureButtonOffset = 65;
    const disableGroupButtonOffset = measureButtonOffset + 28;
    const expandButtonOffset = disableGroupButtonOffset + (!disableGroupingProp ? 28 : 0);
    const searchButtonOffset = disableGroupButtonOffset + (!disableGroupingProp ? 28 : 0) + (expandStyleClass ? 28 : 0);
    const styles: any = createStyles({
        clusterMarker: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            fontFamily: 'DIN Offc Pro Medium',
            backgroundColor: COLORS.PRIMARY,
            color: COLORS.SECONDARY,
            fontSize: '16px',
            textShadow: '0 0 1px ' + COLORS.SECONDARY,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        quotedClusterMarker: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            fontFamily: 'DIN Offc Pro Medium',
            backgroundColor: COLORS.INFO_LIGHT,
            color: COLORS.SECONDARY,
            fontSize: '16px',
            textShadow: '0 0 1px ' + COLORS.SECONDARY,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        submittedClusterMarker: {
            width: 40,
            height: 40,
            borderRadius: '50%',
            fontFamily: 'DIN Offc Pro Medium',
            backgroundColor: COLORS.SUCCESS_LIGHT,
            color: COLORS.SECONDARY,
            fontSize: '16px',
            textShadow: '0 0 1px ' + COLORS.SECONDARY,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        marker: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
        },
        distanceContainer: {
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, .3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            top: 30,
            left: 10,
            bottom: 'auto',
            right: 'auto',
            backgroundColor: '#fff',
            opacity: 0.85,
            padding: '3px',
        },
        distanceToggleContainer: {
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, .3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            top: measureButtonOffset,
            right: 10,
            bottom: 'auto',
            left: 'auto',
            backgroundColor: '#fff',
        },
        toggleButton: {
            padding: '3px',
            border: '0px',
            borderRadius: '0px',
            marginTop: '-1px',
        },
        disableGroupToggleContainer: {
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, .3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            top: disableGroupButtonOffset,
            right: 10,
            bottom: 'auto',
            left: 'auto',
            backgroundColor: '#fff',
        },
        expandMapToggleContainer: {
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, .3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            top: expandButtonOffset,
            right: 10,
            bottom: 'auto',
            left: 'auto',
            backgroundColor: '#fff',
        },
        showSearchToggleContainer: {
            position: 'absolute',
            zIndex: 10,
            boxShadow: '0px 1px 4px rgba(0, 0, 0, .3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            top: searchButtonOffset,
            right: 10,
            bottom: 'auto',
            left: 'auto',
            backgroundColor: '#fff',
        },
        searchBar: {
            zIndex: 10,
            position: 'absolute',
            boxShadow:
                '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
            top: 3,
            left: 400,
            right: 'auto',
            bottom: 'auto',
            backgroundColor: '#f5f5f5',
            width: '400px',
        },
    });

    const selectStyles = makeStyles(() => ({
        select: {
            '&.MuiSelect-standard': {
                padding: '0px',
                color: COLORS.SECONDARY,
                fontWeight: 700,
            },
        },
        'MuiInputBase-input': {
            padding: 0,
        },
    }))();

    // function makeTIVText(TIV: number) {
    //     TIV = Math.round(TIV / 1000000);
    //     let TIVtext = TIV + 'M';
    //     if (TIV / 1000 > 1) {
    //         TIV = Math.round(TIV / 100);
    //         if (TIV % 10 === 0 || TIV / 10 >= 10) {
    //             TIVtext = Math.round(TIV / 10) + 'B';
    //         } else {
    //             TIVtext = TIV / 10 + 'B';
    //         }
    //     }
    //     return TIVtext;
    // }

    function buildClusterMarker(
        getLeaves: (limit?: number, offset?: number) => Array<React.ReactElement>,
        coordinates: number[],
        style: React.CSSProperties
    ) {
        const leaves = getLeaves();
        let TIV = 0;
        let count = 0;
        leaves.forEach((l) => {
            TIV += l.props['data-feature'].properties.TIV;
            count += 1;
        });
        const TIVText = formatTruncatedDollar(TIV);

        return (
            <Marker key={coordinates.toString()} coordinates={coordinates} style={style} min-zoom={zoomThreshold}>
                <div>{installationProvider.showCountOnMapCluster(productLineCode || '') ? count : TIVText}</div>
            </Marker>
        );
    }

    const quotedClusterMarker = (
        coordinates: GeoJSON.Position,
        pointCount: number,
        getLeaves: (limit?: number, offset?: number) => Array<React.ReactElement>
    ) => {
        return buildClusterMarker(getLeaves, coordinates, styles.quotedClusterMarker);
    };

    const submittedClusterMarker = (
        coordinates: GeoJSON.Position,
        pointCount: number,
        getLeaves: (limit?: number, offset?: number) => Array<React.ReactElement>
    ) => {
        return buildClusterMarker(getLeaves, coordinates, styles.submittedClusterMarker);
    };

    const clusterMarker = (
        coordinates: GeoJSON.Position,
        pointCount: number,
        getLeaves: (limit?: number, offset?: number) => Array<React.ReactElement>
    ) => {
        return buildClusterMarker(getLeaves, coordinates, styles.clusterMarker);
    };

    const onStateClusterClick = (cluster: any) => {
        if (measureMode) return;
        setMapState({ zoom: [zoomThreshold + 0.1], center: [cluster.lngLat.lng, cluster.lngLat.lat] });
    };

    const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
        let newList = event.target.value as string[];
        if (selectedList.includes('SUBMISSION') && !newList.includes('SUBMISSION')) {
            newList = ['SUBMISSION'];
        }
        setSelectedList(newList);
    };

    return (
        <>
            <Grid
                container
                className={mapExpanded ? expandStyleClass : ''}
                style={mapExpanded ? {} : { height: '100%' }}
                direction={'column'}
            >
                <Grid container style={{ height: '1.5%' }}>
                    <AppBar
                        style={{ width: '250px', padding: '5px', zIndex: 10 }}
                        position="relative"
                        color={'default'}
                    >
                        <FormControl size={'small'}>
                            <Select
                                variant="standard"
                                classes={selectStyles}
                                value={selectedList}
                                multiple
                                disableUnderline={true}
                                displayEmpty={true}
                                onChange={handleSelectChange}
                                renderValue={(selected: any) => {
                                    const labels: string[] = [];
                                    if (selected.includes('SUBMISSION')) labels.push('Submission');
                                    if (selected.includes('INFORCE')) labels.push('In-Force');
                                    if (selected.includes('QUOTED')) labels.push('Quoted');
                                    if (selected.includes('SUBMITTED')) labels.push('Submitted');
                                    if (!selected.length) labels.push('None Selected');
                                    return labels.join(',');
                                }}
                            >
                                {!!submissionId && <MenuItem value="SUBMISSION">Submission Locations</MenuItem>}
                                <MenuItem value="INFORCE">
                                    <Checkbox checked={selectedList.includes('INFORCE')} />
                                    {`${submissionId ? '&' : ''}In-Force Locations`}
                                </MenuItem>
                                <MenuItem value="QUOTED">
                                    <Checkbox checked={selectedList.includes('QUOTED')} />
                                    <pre>{`${submissionId ? '&' : ''}Quoted Locations`}</pre>
                                </MenuItem>
                                <MenuItem value="SUBMITTED">
                                    <Checkbox checked={selectedList.includes('SUBMITTED')} />
                                    {`${submissionId ? '&' : ''}Submitted Locations`}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </AppBar>
                </Grid>
                <Grid style={{ height: '98.5%', position: 'relative' }}>
                    <Map
                        // style="mapbox://styles/lumenrisk/ck8yulty000zy1itnli75sdam"
                        // style="mapbox://styles/lumenrisk/ck8yulty000zy1itnli75sdam/draft"
                        style="mapbox://styles/lumenrisk/cleptlv3b000j01mn5hkrtq11"
                        center={mapState.center}
                        zoom={mapState.zoom}
                        containerStyle={{ height: '100%' }}
                        onZoom={onZoom}
                        onMove={onMove}
                        onClick={onClick}
                        onMouseMove={onMouseMove}
                        onStyleLoad={(map: MapboxGL.Map) => (mapApi.current = map)}
                        renderChildrenInPortal={true}
                    >
                        <>
                            <Source
                                id="distance_source"
                                geoJsonSource={{
                                    data: measureDistancePoints,
                                    type: 'geojson',
                                }}
                            />
                            <Layer
                                id={'measure-points'}
                                type={'circle'}
                                sourceId={'distance_source'}
                                paint={{
                                    'circle-radius': 5,
                                    'circle-color': COLORS.SECONDARY,
                                }}
                                filter={['in', '$type', 'Point']}
                            />
                            <Layer
                                id={'measure-line'}
                                type={'line'}
                                sourceId={'distance_source'}
                                layout={{
                                    'line-cap': 'round',
                                    'line-join': 'round',
                                }}
                                paint={{
                                    'line-width': 2.5,
                                    'line-color': COLORS.SECONDARY,
                                }}
                                filter={['in', '$type', 'LineString']}
                            />
                            {!!searchResultLocation && (
                                <Marker
                                    style={styles.marker}
                                    coordinates={[
                                        searchResultLocation.properties.locationLon || 0,
                                        searchResultLocation.properties.locationLat || 0,
                                    ]}
                                    data-feature={searchResultLocation}
                                    onClick={() => onLocationMarkerClick(searchResultLocation)}
                                >
                                    <Tooltip title={searchResultLocation.formattedAddress}>
                                        <PlaceIcon fontSize="large" htmlColor={theme.palette.secondary.main} />
                                    </Tooltip>
                                </Marker>
                            )}

                            <ZoomControl />
                            {stateLocationsFetched && selectedList.includes('INFORCE') && (
                                <>
                                    <Source
                                        id="states_source"
                                        geoJsonSource={{
                                            data: stateLocations,
                                            type: 'geojson',
                                        }}
                                    />
                                    <Layer
                                        id="states_circle"
                                        sourceId="states_source"
                                        maxZoom={zoomThreshold}
                                        onClick={onStateClusterClick}
                                        layout={{ visibility: 'visible' }}
                                        paint={{
                                            'circle-color': COLORS.PRIMARY,
                                            'circle-radius': 20,
                                        }}
                                        type="circle"
                                    />
                                    <Layer
                                        id="states_tiv"
                                        type="symbol"
                                        sourceId="states_source"
                                        maxZoom={zoomThreshold}
                                        paint={{
                                            'text-color': COLORS.SECONDARY,
                                        }}
                                        layout={{
                                            'text-field': installationProvider.showCountOnMapCluster(
                                                productLineCode || ''
                                            )
                                                ? `{count}`
                                                : `{TIV}`,
                                            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                                            'text-offset': [0, 0],
                                        }}
                                    />
                                </>
                            )}
                            {locationsFetched &&
                                !reloadingClusters &&
                                currentZoom > zoomThreshold &&
                                selectedList.includes('INFORCE') && (
                                    <>
                                        <Cluster
                                            zoomOnClick={!measureMode}
                                            zoomOnClickPadding={40}
                                            maxZoom={disableGrouping ? 0 : 16}
                                            ClusterMarkerFactory={clusterMarker}
                                        >
                                            {locations.features.map((feature: any, key: number) => (
                                                <Marker
                                                    key={key}
                                                    style={styles.marker}
                                                    coordinates={feature.geometry.coordinates}
                                                    data-feature={feature}
                                                    min-zoom={zoomThreshold}
                                                    onClick={() => onLocationMarkerClick(feature)}
                                                >
                                                    <Tooltip
                                                        title={
                                                            feature.properties.primaryLimit
                                                                ? feature.properties.primaryLimit
                                                                : ''
                                                        }
                                                    >
                                                        <ApartmentIcon
                                                            htmlColor={
                                                                feature.properties.primaryLimit
                                                                    ? theme.palette.error.main
                                                                    : theme.palette.primary.main
                                                            }
                                                        />
                                                    </Tooltip>
                                                </Marker>
                                            ))}
                                        </Cluster>
                                    </>
                                )}
                            {quotedLocationsFetched &&
                                !reloadingClusters &&
                                currentZoom > zoomThreshold &&
                                selectedList.includes('QUOTED') && (
                                    <>
                                        <Cluster
                                            zoomOnClick={!measureMode}
                                            zoomOnClickPadding={40}
                                            maxZoom={disableGrouping ? 0 : 16}
                                            ClusterMarkerFactory={quotedClusterMarker}
                                        >
                                            {quotedLocations.features.map((feature: any, key: number) => (
                                                <Marker
                                                    key={key}
                                                    style={styles.marker}
                                                    coordinates={feature.geometry.coordinates}
                                                    data-feature={feature}
                                                    min-zoom={zoomThreshold}
                                                    onClick={() => onLocationMarkerClick(feature)}
                                                >
                                                    <ApartmentIcon htmlColor={COLORS.INFO_LIGHT} />
                                                </Marker>
                                            ))}
                                        </Cluster>
                                    </>
                                )}
                            {submittedLocationsFetched &&
                                !reloadingClusters &&
                                currentZoom > zoomThreshold &&
                                selectedList.includes('SUBMITTED') && (
                                    <>
                                        <Cluster
                                            zoomOnClick={!measureMode}
                                            zoomOnClickPadding={40}
                                            maxZoom={disableGrouping ? 0 : 16}
                                            ClusterMarkerFactory={submittedClusterMarker}
                                        >
                                            {submittedLocations.features.map((feature: any, key: number) => (
                                                <Marker
                                                    key={key}
                                                    style={styles.marker}
                                                    coordinates={feature.geometry.coordinates}
                                                    data-feature={feature}
                                                    min-zoom={zoomThreshold}
                                                    onClick={() => onLocationMarkerClick(feature)}
                                                >
                                                    <ApartmentIcon htmlColor={COLORS.SUCCESS_LIGHT} />
                                                </Marker>
                                            ))}
                                        </Cluster>
                                    </>
                                )}
                            {submissionLocationsFetched && (
                                <>
                                    {submissionLocations.features.map((feature: any, key: number) => (
                                        <Marker
                                            key={key}
                                            style={styles.marker}
                                            coordinates={feature.geometry.coordinates}
                                            data-feature={feature}
                                            onClick={() => onLocationMarkerClick(feature)}
                                        >
                                            <ApartmentIcon htmlColor={COLORS.PRIMARY_LIGHT} />
                                        </Marker>
                                    ))}
                                </>
                            )}

                            <ScaleControl style={{ bottom: '25px', right: '5px' }} measurement={'mi'} />
                            <Tooltip title={'Toggle Measure Mode'}>
                                <div style={{ ...styles.distanceToggleContainer }}>
                                    <ToggleButton
                                        style={{ ...styles.toggleButton }}
                                        selected={measureMode}
                                        color={'primary'}
                                        value={'measure'}
                                        onClick={onMeasureToggleClick}
                                    >
                                        <StraightenIcon color={'primary'} fontSize={'small'} />
                                    </ToggleButton>
                                </div>
                            </Tooltip>
                            {!disableGroupingProp && (
                                <Tooltip title={'Enable Grouping within State'}>
                                    <div style={{ ...styles.disableGroupToggleContainer }}>
                                        <ToggleButton
                                            style={{ ...styles.toggleButton }}
                                            selected={!disableGrouping}
                                            color={'primary'}
                                            value={'grouping'}
                                            onClick={() => {
                                                setDisableGrouping(!disableGrouping);
                                                setReloadingClusters(true);
                                            }}
                                        >
                                            <GroupWorkIcon color={'primary'} fontSize={'small'} />
                                        </ToggleButton>
                                    </div>
                                </Tooltip>
                            )}
                            {expandStyleClass && !expanded && (
                                <Tooltip title={'Expand Map'}>
                                    <div style={{ ...styles.expandMapToggleContainer }}>
                                        <ToggleButton
                                            style={{ ...styles.toggleButton }}
                                            selected={mapExpanded}
                                            color={'primary'}
                                            value={'expand'}
                                            onClick={onExpandToggleClick}
                                        >
                                            <SettingsOverscanIcon color={'primary'} fontSize={'small'} />
                                        </ToggleButton>
                                    </div>
                                </Tooltip>
                            )}
                            <Tooltip title={'Search Map'}>
                                <div style={{ ...styles.showSearchToggleContainer }}>
                                    <ToggleButton
                                        style={{ ...styles.toggleButton }}
                                        selected={showSearch}
                                        color={'primary'}
                                        value={'search'}
                                        onClick={onSearchToggleClick}
                                    >
                                        <SearchIcon color={'primary'} fontSize={'small'} />
                                    </ToggleButton>
                                </div>
                            </Tooltip>
                            {measureText && (
                                <div style={{ ...styles.distanceContainer }}>
                                    <Typography style={{ margin: '0px' }}>{measureText}</Typography>
                                </div>
                            )}
                        </>
                    </Map>
                    {showSearch && (
                        <div style={{ ...styles.searchBar }}>
                            <TextField
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                style={{ cursor: 'hand' }}
                                                onClick={() => doSearch(searchTerm)}
                                            />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setSearchResultLocation(undefined);
                                                }}
                                                size="large"
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                value={searchTerm}
                                onChange={onSearchChange}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        doSearch(searchTerm);
                                    }
                                    e.stopPropagation();
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                }}
                                fullWidth
                            />
                        </div>
                    )}
                </Grid>
            </Grid>
            {popupLocation && (
                <LocationDetailDialog
                    open={!!popupLocation}
                    onClose={() => setPopupLocation(undefined)}
                    locationData={popupLocation}
                />
            )}
        </>
    );
};

export default withTheme<DefaultTheme, typeof LocationMap>(LocationMap);
